'use client';

import { Check, ChevronLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import { type BalanceDisplayMode, useBalanceDisplayStore } from '@/lib/store/balance-display-store';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { cn } from '@/lib/ui-kit/utils';

interface DisplaySettingsProps {
  /** Shown: focus moves to Back, since the screen it came from goes inert. */
  isActive: boolean;
  onBack: () => void;
}

const THUMB_BAR = 'h-[3px] rounded-[1.5px] bg-v2-text-secondary opacity-50';

/** Miniature of the Total Assets layout: a title bar, a stack of coins, a line. */
function TotalThumb() {
  return (
    <>
      <span className="h-1.5 w-9 rounded-[3px] bg-v2-text-secondary" />
      <span className="flex">
        {['BTC', 'ETH', 'SOL', 'USDT'].map((symbol) => (
          <TokenIcon key={symbol} symbol={symbol} size={12} className="-mr-[3px]" />
        ))}
      </span>
      <span className={cn(THUMB_BAR, 'w-11')} />
    </>
  );
}

/** Miniature of Auto by page: one line per product, each with its own coin. */
function AutoThumb() {
  return (
    <>
      <span className="h-1.5 w-9 rounded-[3px] bg-v2-text-secondary" />
      {['USDT', 'USDC'].map((symbol) => (
        <span key={symbol} className="flex items-center gap-1">
          <TokenIcon symbol={symbol} size={12} />
          <span className={cn(THUMB_BAR, 'w-9')} />
        </span>
      ))}
    </>
  );
}

interface OptionProps {
  mode: BalanceDisplayMode;
  title: string;
  hint: string;
  thumb: ReactNode;
}

function Option({ mode, title, hint, thumb }: OptionProps) {
  const currentMode = useBalanceDisplayStore((s) => s.mode);
  const setMode = useBalanceDisplayStore((s) => s.setMode);
  const isSelected = currentMode === mode;
  const handleSelect = () => setMode(mode);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={handleSelect}
      className={cn(
        'relative flex w-full cursor-pointer items-start gap-2 rounded-[10px] border border-solid p-3 text-left transition-colors',
        isSelected
          ? 'border-v2-btn-secondary-hover bg-v2-neutral-surface dark:bg-white/10'
          : 'border-v2-surface-tertiary hover:bg-v2-neutral-surface dark:hover:bg-white/5',
      )}
    >
      <span className="flex h-[52px] w-16 shrink-0 flex-col gap-1 overflow-hidden rounded-[8px] bg-v2-surface-tertiary p-2 dark:bg-white/10">
        {thumb}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[13px] font-medium leading-4 tracking-[-0.13px] text-v2-text-primary">
          {title}
        </span>
        <span className="text-[12px] leading-[1.15] text-v2-text-secondary">{hint}</span>
      </span>
      {isSelected ? (
        <span className="absolute -top-2 right-[-3px] flex rounded-[20px] bg-v2-surface-tertiary p-2 text-v2-text-primary dark:bg-white/10">
          <Check size={16} />
        </span>
      ) : null}
    </button>
  );
}

/** "What to show" (Figma 15073:84638): the popover's layout, kept per browser. */
export function DisplaySettings({ isActive, onBack }: DisplaySettingsProps) {
  const backRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations('balancePopover');

  useEffect(() => {
    if (isActive) backRef.current?.focus({ preventScroll: true });
  }, [isActive]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          aria-label={t('back')}
          className="-m-1 flex cursor-pointer p-1 text-v2-text-secondary transition-colors hover:text-v2-text-primary"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="min-w-0 flex-1 text-[14px] font-semibold text-v2-text-primary">
          {t('whatToShow')}
        </h3>
      </div>
      <p className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-secondary">
        {t('whatToShowHint')}
      </p>
      <div role="radiogroup" aria-label={t('whatToShow')} className="flex flex-col gap-3">
        <Option
          mode="total"
          title={t('totalAssets')}
          hint={t('modeTotalHint')}
          thumb={<TotalThumb />}
        />
        <Option mode="auto" title={t('modeAuto')} hint={t('modeAutoHint')} thumb={<AutoThumb />} />
      </div>
    </div>
  );
}
